/* eslint-disable @typescript-eslint/dot-notation */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { CrudApiBackend } from '@workflow/class/crud-api-backend';
import { MemoryBackend } from '@workflow/class/memory-backend';
import { SessionStorageBackend } from '@workflow/class/session-storage-backend';
import { PATH_PAGE_ITERATION_SYMBOL, PATH_ITERATION_SYMBOL, StatefulValue } from '@workflow/class/stateful-value.class';
import { FlatData, StorageBackend, ErrCallback } from '@workflow/class/storage-backend.base';
import { Backend, PrimitiveType } from '@workflow/class/workflow.type';
import { StorageInfoService } from '../storage-info/storage-info.service';

export { FlatData, CrudApiBackend, MemoryBackend, SessionStorageBackend };

/** Service to handle storage backends for any StatefulValue */
@Injectable({
  providedIn: 'root'
})
export class StorageService {
  /** Cached array sizes */
  public readonly arraySizes: { [rootPath: string]: number } = {};

  /** Registry of protocols / backend instances */
  public readonly backends: { [protocol: string]: StorageBackend } = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly backendsInitPromise: { [protocol: string]: Promise<any> } = {};

  /** Cached data */
  public readonly data: FlatData = {};

  /** Data update propagation */
  public readonly statefulValuesUpdate = new Subject<FlatData>();

  /** Data update propagation */
  public readonly backendInit$ = new BehaviorSubject<boolean>(false);

  constructor(private readonly storageInfoService: StorageInfoService) {}

  /**
   * Register a backend instance
   * @param protocol part of the storage if before the ":". E.g. "mem" in "mem:x/y/z"
   * @param type storage type
   * @param endpoint path for API storage
   */
  public addBackend(protocol: string, type: Backend, endpoint?: string, token?: string): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const klass = { api: CrudApiBackend, mem: MemoryBackend, ss: SessionStorageBackend }[type] as new (...args: any[]) => StorageBackend;
    this.backends[protocol] = new klass(protocol, this.data, this.arraySizes, endpoint, token);
    this.backends[protocol].propagateBundle.subscribe(() => {
      this.statefulValuesUpdate.next(this.data);
    });
    this.backends[protocol].updateArraySizes.subscribe((resetAll) => {
      this.updateArraySizes(resetAll);
    });
  }

  /**
   * Guaranteed return of clean array root path (exluding the '$' and the '/' before it if present)
   * @param storageId full path, optionally including the `$`
   */
  public arrayRootPathOf(storageId: string, pageIterationId?: number): string {
    if (storageId.indexOf(PATH_PAGE_ITERATION_SYMBOL) > -1 && pageIterationId == null) {
      throw new Error('Page iteration ID is required for iterated storage ID: ' + storageId);
    }
    if (storageId.indexOf(PATH_PAGE_ITERATION_SYMBOL) < 0 && pageIterationId != null) {
      throw new Error('No page iteration for storage ID: ' + storageId);
    }
    const root = storageId.split(PATH_ITERATION_SYMBOL)[0];
    const last = root.length - 1;
    const rootPath = root.substr(last) === '/' ? root.substr(0, last) : root;
    return pageIterationId == null ? rootPath : rootPath.replace(PATH_PAGE_ITERATION_SYMBOL, String(pageIterationId));
  }

  /**
   * Guaranteed return of clean array root path (excluding the '$' and the '/' before it if present)
   * Example: api:address/$/state -> api:address
   * Example: api:address/0/state -> api:address/0/state
   * Example: api:address/$ -> api:address
   *
   * @param storageId full path, optionally including the `$`
   */
  public pageIterationArrayRootPathOf(storageId: string): string {
    const root = storageId.split(PATH_PAGE_ITERATION_SYMBOL)[0];
    const last = root.length - 1;
    return root.substr(last) === '/' ? root.substr(0, last) : root;
  }

  /**
   * Expand array on backend
   * Will check if backend has its own implementation, and call that, if so
   * @param storageId the path before the '$'
   * @param appendData object {fullpath: value} to save on newly created array item
   * @param cb callback function
   */
  public arrayAdd(storageId: string, cb?: ErrCallback): void;
  public arrayAdd(storageId: string, appendData: FlatData, cb?: ErrCallback): void;
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  public arrayAdd(storageId: string, arg1: FlatData | ErrCallback, arg2?: ErrCallback): void {
    const rootPath = this.arrayRootPathOf(storageId);
    const backend = this.backends[storageId.split(':')[0]];
    if (backend['arrayAdd']) {
      return backend['arrayAdd'](rootPath.split(':')[1], arg1, arg2);
    }
    const cb = arg2 || (typeof arg1 === 'function' && arg1);
    const appendData = typeof arg1 === 'object' && arg1;
    this.arraySizes[rootPath] = (this.arraySizes[rootPath] || 0) + 1;
    if (appendData && typeof appendData === 'object') {
      const idx = this.arraySizes[rootPath] - 1;
      for (const [appendPath, value] of Object.entries(appendData)) {
        if (appendPath.indexOf(PATH_ITERATION_SYMBOL) < 0) {
          throw new Error('appendData pathes must include $');
        }
        this.data[appendPath.replace(PATH_ITERATION_SYMBOL, String(idx))] = value;
      }
    }
    this.statefulValuesUpdate.next(this.data);
    if (cb) {
      cb();
    }
  }

  /** Method returns whether all backend initialized */
  public isAllBackendInitialized(): boolean {
    if (!Object.keys(this.backends).length) return false;
    return Object.values(this.backends).every((backend) => backend.initialized);
  }

  /**
   * Remove array index on backend
   * Will check if backend has its own implementation, and call that, if so
   * @param storageId the path before the '$'
   * @param index 0-based element index (last index on null)
   * @param cb callback function
   */
  public arrayRemove(storageId: string, index?: number, cb?: ErrCallback): void {
    const rootPath = this.arrayRootPathOf(storageId);
    const backend = this.backends[storageId.split(':')[0]];
    if (!this.arraySizes.hasOwnProperty(rootPath)) {
      if (cb) {
        cb(new Error('No such array: ' + storageId));
      }
      return;
    }
    if (backend['arrayRemove']) {
      return backend['arrayRemove'](rootPath.split(':')[1], index, cb);
    }
    const last = this.arraySizes[rootPath] - 1;
    const idx = index == null ? last : index;
    for (let i = last; i > idx; i--) {
      for (const indexPath of this.arrayIndexItems(rootPath, i - 1)) {
        delete this.data[indexPath];
      }
      const len = rootPath.length;
      for (const indexPath of this.arrayIndexItems(rootPath, i)) {
        let targetPath = indexPath.substr(0, len);
        if (indexPath[len] === '/') {
          targetPath += `/${i - 1}${indexPath.substr(len + 1 + String(i).length)}`;
        } else {
          targetPath += `${i - 1}${indexPath.substr(len + String(i).length)}`;
        }
        this.data[targetPath] = this.data[indexPath];
      }
    }
    for (const indexPath of this.arrayIndexItems(rootPath, last)) {
      delete this.data[indexPath];
    }
    this.arraySizes[rootPath]--;
    this.statefulValuesUpdate.next(this.data);
    if (cb) {
      cb();
    }
  }

  /**
   * Empty an array on backend
   * Will check if backend has its own implementation, and call that, if so
   * @param storageId the path before the '$'
   * @param cb callback function
   */
  public arrayTruncate(storageId: string, cb?: ErrCallback): void {
    const rootPath = this.arrayRootPathOf(storageId);
    const backend = this.backends[storageId.split(':')[0]];
    if (!this.arraySizes.hasOwnProperty(rootPath)) {
      if (cb) {
        cb(new Error('No such array: ' + storageId));
      }
      return;
    }
    if (backend['arrayTruncate']) {
      return backend['arrayTruncate'](rootPath.split(':')[1], cb);
    }
    for (let i = this.arraySizes[rootPath] - 1; i >= 0; i--) {
      for (const indexPath of this.arrayIndexItems(rootPath, i - 1)) {
        delete this.data[indexPath];
      }
    }
    this.arraySizes[rootPath] = 0;
    this.statefulValuesUpdate.next(this.data);
    if (cb) {
      cb();
    }
  }

  /**
   * Return all full data path's for an array index
   * @param path storage ID
   * @param index index of array to collect
   */
  public arrayIndexItems(path: string, index: number): string[] {
    const rootPath = this.arrayRootPathOf(path);
    const list: string[] = [];
    for (const key of Object.keys(this.data).filter((id) => id.substr(0, rootPath.length) === rootPath)) {
      const info = this.storageInfoService.parse(key);
      if (info && info.iterationId != null) {
        const arrayRootPath = this.arrayRootPathOf(info.id, info.pageIterationId);
        if (arrayRootPath === rootPath && info.iterationId === index) {
          list.push(info.id);
        }
        this.arraySizes[arrayRootPath] = Math.max(info.iterationId + 1, this.arraySizes[arrayRootPath] || 0);
      }
    }
    return list;
  }

  /**
   * clear data from backend
   */
  public clear(): void {
    Object.keys(this.data).forEach((key) => {
      delete this.data[key];
    });
    Object.keys(this.backends).forEach((protocol) => this.backends[protocol].clear && this.backends[protocol].clear());
    this.backendInit$.observers = [];
  }

  /**
   * clear data from backend by key
   */
  public clearBackendByKey(sessionKey: string, backendKey: string): void {
    if (!this.data[sessionKey]) {
      console.error(`Unable to find backend: ${sessionKey}`);
      return;
    }
    delete this.data[sessionKey];
    if (this.backends[backendKey] && this.backends[backendKey].clear) {
      this.backends[backendKey].clear();
    }
  }

  /**
   * Request init with authorization for API backends
   * @param authorization session ID
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public initAllBackends(publishEvent = true): Promise<any> {
    if (!Object.keys(this.backends).length) return;
    Object.keys(this.backends).forEach((key) => {
      const initPromise = this.backends[key].init();
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      if (initPromise && !this.backendsInitPromise[key]) {
        this.backendsInitPromise[key] = initPromise;
      }
    });

    return Promise.all(Object.values(this.backendsInitPromise)).then(() => {
      if (publishEvent) this.backendInit$.next(true);
    });
  }

  /**
   * Load field data from backend
   * @param storageId backend id
   * @param callback function ref to handle error or incoming data
   */
  public load(storageId: string, callback: (err?: Error, data?: PrimitiveType) => void): void {
    const [protocol, path] = storageId.split(':');
    this.backends[protocol].load(path, callback);
  }

  /**
   * Save field data to backend
   * @param storageId backend id
   * @param value value to save
   * @param callback function ref to handle success or error message
   */
  public save(storageId: string, value: PrimitiveType, callback: ErrCallback): void {
    const [protocol, path] = storageId.split(':');
    this.backends[protocol].save(path, value, callback);
  }

  public getTokenStorage(protocol: string): StatefulValue {
    const backend = this.backends[protocol];
    if (!backend || backend.type !== 'api' || !backend['tokenStorage']) {
      throw new Error(`the ${protocol} must been defined as an API backend in storage-info.json.`);
    }

    return backend['tokenStorage'];
  }

  /**
   * Update array size cache
   * NOTE: must be triggered after all data changes to keep it up-to-date
   */
  protected updateArraySizes(resetAll?: boolean): void {
    if (resetAll) {
      for (const path of Object.keys(this.arraySizes)) {
        this.arraySizes[path] = 0;
      }
    }
    for (const key of Object.keys(this.data)) {
      const info = this.storageInfoService.parse(key);
      const protocol = key.split(':')[0];
      if (info && info.iterationId != null && info.pageIterationId == null) {
        const arrayRootPath = this.arrayRootPathOf(`${protocol}:${info.id}`);
        this.arraySizes[arrayRootPath] = Math.max(info.iterationId + 1, this.arraySizes[arrayRootPath] || 0);
      } else if (info && info.pageIterationId != null) {
        const pageArrayRootPath = this.pageIterationArrayRootPathOf(`${protocol}:${info.id}`);
        this.arraySizes[pageArrayRootPath] = Math.max(info.pageIterationId + 1, this.arraySizes[pageArrayRootPath] || 0);
        if (info.iterationId != null) {
          const arrayRootPath = this.arrayRootPathOf(`${protocol}:${info.id}`, info.pageIterationId);
          this.arraySizes[arrayRootPath] = Math.max(info.iterationId + 1, this.arraySizes[arrayRootPath] || 0);
        }
      }
    }
  }
}
